<template lang="pug">
  .consultation-list
    n-delete-dialog(
      :value="!!deletingItem"
      :seconds="0"
      @ok="handleDeleteConsultation"
      @close="deletingItem = null"
    )
      span.primary--text Wollen Sie die Beratung "{{ deletingItem ? deletingItem.name: '' }}"" wirklich löschen?

    h2.primary--text Liste Beratungen

    v-row.align-center.mt-4
      v-col(cols="6")
        v-text-field.pt-0(
          v-model="search"
          append-icon="search"
          placeholder="Suchen"
          outlined
          single-line
          hide-details
        )

      v-col.text-right(v-if="selectedCompany" cols="6")
        v-btn(color="primary" @click="gotoRoute('consultation-add')") + Neue Beratung für {{ selectedCompany.name }} anlegen

    v-data-table.mt-4(
      v-bind="tableProps"
      :headers="headers"
      :items="consultations"
      :search="search"
      :options="defaultPagination"
    )
      template(#item="{ item }")
        tr(:class="item.step === 'hidden' && 'hidden-row'")
          td {{ item.id }}
          td {{ item.name }}
          td {{ item.description }}
          td.text-center {{ formatDateTime(item.date_created, 'DD.MM.YYYY') }}
          td.text-center {{ item.rule_description }}

          td: .d-flex.align-center.justify-end
            a.d-flex.mr-1(title="Beratung bearbeiten" @click.prevent="onEditConsultation(item.id)")
              i.fo.fo-large.fo-pencil

            a.d-flex.mr-1(title="Beratung kopieren" @click.prevent="onShowCopyModal(item.id)")
              v-icon(color="primary" size="18") content_copy

            a.d-flex.mr-1(
              title="Beratung löschen"
              @click.prevent="deletingItem = item"
            )
              i.fo.fo-large.fo-trash-bin

      template(#footer.page-text="{ pageStart, pageStop }")
        span Von {{ pageStart }} bis {{ pageStop }}

    v-dialog(v-model="showCopyModal" width="650" persistent)
      v-card.primary--text
        v-card-title Beratung kopieren

        v-form.pa-6(ref="form-copy" lazy-validation @submit.prevent="() => {}")
          p Bitte geben Sie die Daten der neuen, kopierten Beratung an

          v-combobox.mt-8(
            v-if="ruleEngines.length !== 1"
            v-model="copyForm.rule_engine"
            label="Regelwerk"
            outlined
            dense
            :items="ruleEngines"
            :item-text="`description`"
            :item-value="`id`"
          )
            template(#item="{ item }") {{ item.id }} - {{ item.description }} ({{ formatDateTime(item.created_at, 'DD.MM.YYYY HH:mm:ss') }})

          v-text-field.mt-3(
            v-model="copyForm.name"
            label="Name"
            outlined
            dense
            :rules="[ rules.required ]"
          )

          v-text-field.mt-3(
            v-model="copyForm.description"
            label="Beschreibung"
            outlined
            dense
          )

          v-checkbox.mt-3(
            v-model="copyForm.update_company_structure"
            label="Unternehmens-Struktur (Standorte/Gebäude) abgleichen"
            hide-details
          )

          v-checkbox.mt-3(
            v-model="copyForm.update_contacts"
            label="Ansprechpartner abgleichen"
            hide-details
          )

        v-card-actions.pa-5
          v-btn.btn-confirm-flat(color="secondary" large :loading="loading" @click="onCopyConsultation")
            span.primary--text Kopieren

          v-spacer

          v-btn(color="secondary" large outlined :disabled="loading" @click="showCopyModal = false")
            span.primary--text Abbrechen

</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { mapActions } from 'vuex'

import FormMixin from '@/mixins/form'
import Urls from '@/router/urls'

@Component({
  mixins: [
    FormMixin
  ],

  methods: mapActions([
    'selectCompany'
  ])
})
export default class ConsultationList extends Vue {
  search = ''
  consultations = []
  headers = [
    {
      text: 'ID',
      align: 'left',
      sortable: true,
      value: 'id'
    },
    {
      text: 'Name',
      align: 'left',
      sortable: true,
      value: 'name'
    },
    {
      text: 'Beschreibung',
      align: 'left',
      sortable: true,
      value: 'description'
    },
    {
      text: 'Erstellt am',
      align: 'center',
      sortable: true,
      value: 'date_created'
    },
    {
      text: 'Regelwerk',
      align: 'center',
      sortable: true,
      value: 'rule_description'
    },
    {
      text: 'Aktion',
      align: 'right',
      sortable: false,
      value: '#'
    },
  ]

  ruleEngines = []

  copyForm = {
    rule_engine: null,
    name: '',
    description: '',
    update_company_structure: true,
    update_contacts: true
  }

  showCopyModal = false
  loading = false
  deletingItem = null

  created () {
    this.loadConsultations()
    this.loadRuleEngines()
  }

  loadRuleEngines () {
    return this.$axios
      .get(Urls.RuleEngine, {
        only_active: true
      })
      .then(res => {
        this.ruleEngines = res.data || []

        if (this.ruleEngines.length === 1) {
          this.copyForm.rule_engine = this.ruleEngines[0]
        }
      })
  }

  loadConsultations () {
    this.$axios
      .get(Urls.Consultation())
      .then(res => {
        this.consultations = res.data || []
      })
  }

  onEditConsultation (itemId) {
    this.$router.push({
      name: 'consultation-edit',
      params: {
        id: itemId
      }
    })
  }

  handleDeleteConsultation () {
    this.loading = true

    this.$axios
      .delete(Urls.Consultation(this.deletingItem.id))
      .then(() => {
        this.loadConsultations()
        this.deletingItem = null
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
      .finally(() => {
        this.loading = false
      })
  }

  onShowCopyModal (itemId) {
    this.copyItemId = itemId
    this.showCopyModal = true
  }

  onCopyConsultation () {
    if (!this.isFormValid('form-copy')) {
      return
    }

    const params = {
      rule_engine_id: this.copyForm.rule_engine
        ? this.copyForm.rule_engine.id
        : null,
      name: this.copyForm.name,
      description: this.copyForm.description,
      update_company_structure: this.copyForm.update_company_structure,
      update_contacts: this.copyForm.update_contacts
    }

    this.loading = true

    this.$axios
      .post(`${ Urls.Consultation(this.copyItemId) }copy/`, params)
      .then((res) => {
        this.onEditConsultation(res.data.id)
      })
      .finally(() => {
        this.showCopyModal = false
        this.loading = false
      })
  }
}
</script>

<style lang="sass">
tr.hidden-row
  background-color: #F5A9A9
</style>
